export interface CachedSummary {
  summary: string;
  timestamp: number;
  ttl: number;
}

export class SummaryCache {
  private static readonly CACHE_PREFIX = 'ouibeef_summary_';
  private static readonly DEFAULT_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

  private static generateKey(workTitle: string, philosopher: string): string {
    return `${this.CACHE_PREFIX}${philosopher}_${workTitle.replace(/\s+/g, '_').toLowerCase()}`;
  }

  static get(workTitle: string, philosopher: string): string | null {
    try {
      const key = this.generateKey(workTitle, philosopher);
      const cached = localStorage.getItem(key);
      
      if (!cached) {
        return null;
      }

      const data: CachedSummary = JSON.parse(cached);
      const now = Date.now();
      
      if (now - data.timestamp > data.ttl) {
        // Cache expired, remove it
        localStorage.removeItem(key);
        return null;
      }
      
      return data.summary;
    } catch (error) {
      console.error('Error reading from summary cache:', error);
      return null;
    }
  }

  static set(workTitle: string, philosopher: string, summary: string, ttl?: number): void {
    try {
      const key = this.generateKey(workTitle, philosopher);
      const data: CachedSummary = {
        summary,
        timestamp: Date.now(),
        ttl: ttl || this.DEFAULT_TTL
      };
      
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Error writing to summary cache:', error);
      // If localStorage is full or unavailable, continue without caching
    }
  }

  static clear(workTitle?: string, philosopher?: string): void {
    try {
      if (workTitle && philosopher) {
        // Clear specific entry
        const key = this.generateKey(workTitle, philosopher);
        localStorage.removeItem(key);
      } else {
        // Clear all summary cache entries
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith(this.CACHE_PREFIX)) {
            localStorage.removeItem(key);
          }
        });
      }
    } catch (error) {
      console.error('Error clearing summary cache:', error);
    }
  }

  static getCacheSize(): number {
    let count = 0;
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(this.CACHE_PREFIX)) {
        count++;
      }
    });
    return count;
  }
}